import React, { useEffect, useState } from 'react'
import Wrapper from '../../components/Wrapper'
import {
    Paper,
    Typography,
    Box,
    IconButton,
    Button,
    Alert,
    TextField,
    Chip,
    Divider,
    CircularProgress,
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import InfoIcon from '@mui/icons-material/Info'
import { useNavigate } from 'react-router-dom'
import { input, shortFunction } from '../../translate'
import { customStyles, greenTheme } from '../../helpers/style'
import Select from 'react-select'
import Api from '../../Api'
import { toast } from 'react-toastify'

const { REACT_APP_SERVER } = process.env
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const emptyForm = { email: '', shortNumbers: [] }

function shortLabel(item) {
    return item?.name ? `${item.number} · ${item.name}` : item?.number || ''
}

function parseEmails(sendMail) {
    if (!sendMail) return []
    const list = Array.isArray(sendMail) ? sendMail : String(sendMail).split(',')
    return list.map((email) => String(email).trim()).filter(Boolean)
}

function mapCallbackMail(rows) {
    const list = Array.isArray(rows) ? rows : []
    const shorts = list.map((row) => ({
        id: row.id,
        number: String(row.virtual ?? ''),
        name: row.name || '',
        hasMail: parseEmails(row.send_mail).length > 0,
    }))
    const byEmail = new Map()
    list.forEach((row) => {
        parseEmails(row.send_mail).forEach((email) => {
            const key = email.toLowerCase()
            if (!byEmail.has(key)) byEmail.set(key, { id: key, email, shortNumbers: [] })
            byEmail.get(key).shortNumbers.push(row.id)
        })
    })
    return { shorts, recipients: [...byEmail.values()] }
}

export default function CallbackNotification({ embedded = false, disabled = false }) {
    const navigate = useNavigate()
    const langIndex = +localStorage.getItem('atsLang') || 1
    const t = (key) => shortFunction[key]?.[langIndex] || shortFunction[key]?.[1] || ''

    const [shortNumbers, setShortNumbers] = useState([])
    const [recipients, setRecipients] = useState([])
    const [form, setForm] = useState(emptyForm)
    const [editingId, setEditingId] = useState(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(!disabled)

    useEffect(() => {
        if (disabled) {
            setLoading(false)
            return
        }
        let active = true
        ;(async () => {
            try {
                const { data } = await Api.getCallbackMail()
                if (!active) return
                const mapped = mapCallbackMail(data?.data)
                setShortNumbers(mapped.shorts)
                setRecipients(mapped.recipients)
            } catch (e) {
                if (active) toast.error(e?.response?.data?.message || 'Error')
            } finally {
                if (active) setLoading(false)
            }
        })()
        return () => { active = false }
    }, [disabled])

    const resetForm = () => {
        setForm(emptyForm)
        setEditingId(null)
        setError('')
    }

    const shortOptions = shortNumbers
        .filter((item) => !item.hasMail || form.shortNumbers.includes(item.id))
        .map((item) => ({ value: item.id, label: shortLabel(item) }))
    const selectedShorts = shortOptions.filter((option) => form.shortNumbers.includes(option.value))

    const handleSubmit = async (ev) => {
        ev.preventDefault()
        const email = form.email.trim()
        if (!EMAIL_RE.test(email)) {
            setError(t('callbackNotifyEmailInvalid'))
            return
        }
        if (!form.shortNumbers.length) {
            setError(t('callbackNotifyShortRequired'))
            return
        }
        const duplicate = recipients.some(
            (r) => r.id !== editingId && r.email.toLowerCase() === email.toLowerCase()
        )
        if (duplicate) {
            setError(t('callbackNotifyDuplicate'))
            return
        }

        try {
            if (editingId) {
                await Api.editCallbackMail({ id: editingId, email, shortNumbers: form.shortNumbers })
            } else {
                await Api.createCallbackMail({ email, shortNumbers: form.shortNumbers })
            }
            const { data } = await Api.getCallbackMail()
            const mapped = mapCallbackMail(data?.data)
            setShortNumbers(mapped.shorts)
            setRecipients(mapped.recipients)
            resetForm()
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Error')
        }
    }

    const handleEdit = (recipient) => {
        setEditingId(recipient.id)
        setForm({ email: recipient.email, shortNumbers: [...recipient.shortNumbers] })
        setError('')
    }

    const handleRemove = async (recipient) => {
        if (!window.confirm(t('callbackNotifyRemoveConfirm'))) return
        try {
            await Api.deleteCallbackMail(recipient.email)
            const { data } = await Api.getCallbackMail()
            const mapped = mapCallbackMail(data?.data)
            setShortNumbers(mapped.shorts)
            setRecipients(mapped.recipients)
            if (editingId === recipient.id) resetForm()
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Error')
        }
    }

    const section = (
        <Box sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                <Paper sx={{ p: embedded ? 0 : { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }} elevation={embedded ? 0 : 1}>
                    {!embedded && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5 }}>
                        <IconButton
                            size="small"
                            sx={{ color, fontSize: 18, '&:hover': { backgroundColor: `${color}15` } }}
                            onClick={() => navigate('/short_function')}
                            aria-label="Back"
                        >
                            <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
                            {t('callbackNotify')}
                        </Typography>
                    </Box>}
                    {embedded && (
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, m: 0 }}>
                            {t('callbackNotify')}
                        </Typography>
                    )}

                    <Alert
                        icon={<InfoIcon />}
                        severity="success"
                        sx={
                            REACT_APP_SERVER !== 'ATS'
                                ? { mb: 1 }
                                : {
                                    mb: 1,
                                    border: `1px solid ${color}`,
                                    backgroundColor: `${color}10`,
                                    '& .MuiAlert-icon': { color },
                                }
                        }
                    >
                        <Typography variant="body2">{t('callbackNotifyDesc')}</Typography>
                    </Alert>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color }} />
                        </Box>
                    ) : null}

                    {!loading && <>
                    <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                            {t('callbackNotifyForm')}
                        </Typography>
                        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                size="small"
                                label={t('callbackNotifyEmail')}
                                placeholder="name@company.com"
                                value={form.email}
                                onChange={(e) => {
                                    setForm((prev) => ({ ...prev, email: e.target.value }))
                                    setError('')
                                }}
                                error={!!error && !EMAIL_RE.test(form.email.trim())}
                                sx={{ maxWidth: 420 }}
                            />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {t('callbackNotifyShorts')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('callbackNotifySelected')}: {form.shortNumbers.length}
                                    </Typography>
                                </Box>
                                <Select
                                    isMulti
                                    isSearchable
                                    options={shortOptions}
                                    value={selectedShorts}
                                    onChange={(options) => {
                                        setForm((prev) => ({ ...prev, shortNumbers: (options || []).map((option) => option.value) }))
                                        setError('')
                                    }}
                                    placeholder={input.select[langIndex]}
                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...customStyles,
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: 40,
                                            borderColor: state.isFocused ? color : '#e5e7eb',
                                            boxShadow: state.isFocused ? `0 0 0 1px ${color}` : 'none',
                                            '&:hover': { borderColor: color },
                                        }),
                                        multiValue: (base) => ({ ...base, backgroundColor: `${color}14` }),
                                        multiValueLabel: (base) => ({ ...base, color, fontWeight: 600 }),
                                        option: (base, state) => ({
                                            ...base,
                                            backgroundColor: state.isSelected || state.isFocused ? `${color}26` : base.backgroundColor,
                                            color: '#000',
                                        }),
                                    }}
                                />
                            </Box>

                            {error ? (
                                <Typography variant="body2" sx={{ color: '#ce405f' }}>{error}</Typography>
                            ) : null}

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{ textTransform: 'none', bgcolor: color, '&:hover': { bgcolor: hoverColor } }}
                                >
                                    {editingId ? t('callbackNotifySave') : t('callbackNotifyAdd')}
                                </Button>
                                {editingId ? (
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        onClick={resetForm}
                                        sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                    >
                                        {t('callbackNotifyCancel')}
                                    </Button>
                                ) : null}
                            </Box>
                        </Box>
                    </Paper>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                            {t('callbackNotifyList')}
                        </Typography>
                        {recipients.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">{t('callbackNotifyEmpty')}</Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {recipients.map((recipient) => (
                                    <Paper
                                        key={recipient.id}
                                        elevation={0}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: editingId === recipient.id ? color : '#e5e7eb',
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                            justifyContent: 'space-between',
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                                {recipient.email}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {recipient.shortNumbers.map((id) => {
                                                    const item = shortNumbers.find((s) => s.id === id)
                                                    if (!item) return null
                                                    return (
                                                        <Chip
                                                            key={id}
                                                            size="small"
                                                            label={shortLabel(item)}
                                                            sx={{ bgcolor: `${color}14`, color, fontWeight: 600 }}
                                                        />
                                                    )
                                                })}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleEdit(recipient)}
                                                sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                            >
                                                {t('callbackNotifyEdit')}
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleRemove(recipient)}
                                                sx={{ textTransform: 'none', color: '#ce405f', borderColor: '#ce405f', '&:hover': { borderColor: '#a8324c', color: '#a8324c' } }}
                                            >
                                                {t('callbackNotifyRemove')}
                                            </Button>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        )}
                    </Box>
                    </>}
                </Paper>
        </Box>
    )

    if (embedded) return section

    return (
        <Wrapper>
            <div className="tableParent">
                {section}
            </div>
        </Wrapper>
    )
}
